import type {
  PlanCatalogFeatures,
  PlanCatalogItem,
} from "@/core/api/billingApi";
import type { PlanFeatureKey } from "@/core/hooks/planFeature";
import type { SectorPlanFeatures } from "@/types";

/**
 * O que o bloqueio por plano oferece (SA-1401): o plano atual e o plano
 * mínimo pelo nome de exibição, e o que se libera subindo de um para o outro.
 *
 * `requiredPlanName` nulo é o fail-closed de oferta: o catálogo falhou, o
 * plano atual é legado (fora do catálogo ou sem preço) ou nenhum plano
 * vendável acima dele tem o recurso. Quem mostra o bloqueio não nomeia plano
 * nenhum nesse caso (nunca inventa um). `currentPlanName` nulo: o plano atual
 * não tem nome que se possa mostrar (sem slug, ou slug sem forma de nome).
 */
export type PlanUpgradeOffer = {
  currentPlanName: string | null;
  requiredPlanName: string | null;
  /**
   * Slug do plano nomeado (mesma decisão do nome): o aviso de trial e o modal
   * D-3 levam a `/ativar-plano?plan=<slug>`. `null` sempre que o nome é nulo.
   */
  requiredPlanSlug: string | null;
  unlocks: string[];
};

/* Ordem e rótulos do `planUnlocks()` do protótipo (docs/design-system/
   Atendimento.dc.html): primeiro os recursos, depois os limites. */
const FEATURE_BENEFITS: readonly [keyof PlanCatalogFeatures, string][] = [
  ["hasFollowUps", "Follow-ups automáticos"],
  ["hasIntegrations", "Integrações"],
  ["hasBulkSend", "Disparos em massa"],
  ["hasCustomIntegrations", "Integrações personalizadas"],
];

/** "Mini (legado R$ 449)" → "Mini": o sufixo entre parênteses é interno. */
export function displayPlanName(name: string): string {
  const trimmed = name.trim();
  const withoutSuffix = trimmed.replace(/\s*\([^)]*\)$/, "").trim();
  return withoutSuffix || trimmed;
}

// Segmentos de variante no fim do slug: "legacy", preço ("449"), desconto
// ("10off"). O que sobra é o nome do plano.
const VARIANT_SEGMENT = /^(legacy|\d+|\d+off)$/;
const NAME_SEGMENT = /^[a-z]+$/;

/**
 * Nome de exibição de um plano que não está no catálogo (legado, variante),
 * derivado do slug: "mini-legacy-449" → "Mini". Slug que não tem forma de
 * nome (caractere fora de a-z, só números) devolve `null`: melhor não nomear
 * o plano do que mostrar um nome inventado.
 */
export function planNameFromSlug(slug: string | null): string | null {
  const segments = (slug ?? "").trim().toLowerCase().split("-");
  while (
    segments.length > 1 &&
    VARIANT_SEGMENT.test(segments[segments.length - 1])
  ) {
    segments.pop();
  }
  if (!segments.every((segment) => NAME_SEGMENT.test(segment))) return null;
  return segments
    .map((segment) => segment[0].toUpperCase() + segment.slice(1))
    .join(" ");
}

// Limite nulo é "sem limite": vale mais que qualquer número.
const limitValue = (limit: number | null) => limit ?? Number.POSITIVE_INFINITY;

const byLeadsThenPrice = (a: PlanCatalogItem, b: PlanCatalogItem) =>
  limitValue(a.leads_per_month_limit) - limitValue(b.leads_per_month_limit) ||
  limitValue(a.price_brl_cents) - limitValue(b.price_brl_cents);

function whatsappsBenefit(
  required: number | null,
  current: number | null
): string | null {
  if (current === null) return null;
  if (required === null) return "WhatsApps conectados ilimitados";
  if (required <= current) return null;
  return required === 1
    ? "1 WhatsApp conectado"
    : `${required} WhatsApps conectados`;
}

function leadsBenefit(
  required: number | null,
  current: number | null
): string | null {
  if (required === null || current === null || required <= current) {
    return null;
  }
  return `Até ${required.toLocaleString("pt-BR")} leads/contatos por mês`;
}

type CurrentPlanInput = {
  feature: PlanFeatureKey;
  /** `plan_slug` do access-state. */
  currentSlug: string | null;
  /** `plan_features` do access-state (a verdade sobre o plano da conta). */
  currentFeatures: SectorPlanFeatures | null;
};

type BuildPlanUpgradeOfferInput = CurrentPlanInput & {
  /** Catálogo de planos visíveis (`/billing/plans`); vazio quando falhou. */
  plans: readonly PlanCatalogItem[];
  /**
   * `plan_price_verified` do access-state: a conta paga o preço de LISTA do
   * plano (o servidor compara o price da assinatura com o do plano). O front só
   * vê a lista, então sem isto nenhum plano é nomeado.
   */
  currentPriceVerified: boolean;
};

/** Nenhum plano a nomear: o bloqueio usa a copy genérica, sem benefícios. */
const NO_OFFER: PlanUpgradeOffer = {
  currentPlanName: null,
  requiredPlanName: null,
  requiredPlanSlug: null,
  unlocks: [],
};

/**
 * A oferta depende do catálogo? Só quando o plano atual é conhecido e (pelo
 * access-state) não tem o recurso. Sem plano atual, ou com o plano atual já
 * tendo o recurso, a resposta é "nenhum plano" sem precisar do catálogo.
 * `currentFeatures` nulo com slug: o catálogo diz o que o plano tem.
 */
export function offerNeedsCatalog({
  feature,
  currentSlug,
  currentFeatures,
}: CurrentPlanInput): boolean {
  return Boolean(currentSlug) && !currentFeatures?.[feature];
}

/**
 * O plano da oferta segura (a regra está documentada no
 * `buildPlanUpgradeOffer`, que a usa): o plano visível de menor limite de
 * leads com o recurso, acima do atual e com preço maior ou igual. `null` em
 * todo caso de fail-closed.
 */
function planUpgradeTarget({
  feature,
  plans,
  currentSlug,
  currentFeatures,
  currentPriceVerified,
}: BuildPlanUpgradeOfferInput): PlanCatalogItem | null {
  if (!currentSlug) return null;

  const currentPlan = plans.find((plan) => plan.slug === currentSlug);
  const ownedFeatures: SectorPlanFeatures =
    currentFeatures ?? currentPlan?.features ?? {};
  if (ownedFeatures[feature]) return null;

  const currentPrice = currentPlan?.price_brl_cents ?? null;
  if (!currentPlan || currentPrice === null || !currentPriceVerified) {
    return null;
  }

  const currentLeads = currentPlan.leads_per_month_limit;
  return (
    plans
      .filter(
        (plan) =>
          plan.slug !== currentSlug &&
          plan.features[feature] &&
          limitValue(plan.leads_per_month_limit) > limitValue(currentLeads) &&
          // Guarda de preço: "acima" nunca custa menos do que o cliente paga.
          plan.price_brl_cents !== null &&
          plan.price_brl_cents >= currentPrice
      )
      .sort(byLeadsThenPrice)[0] ?? null
  );
}

/**
 * Plano mínimo com o recurso e o que ele libera, a partir do catálogo e do
 * access-state (nunca de nome ou preço fixo no código).
 *
 * Fail-closed de oferta (skill superagents-billing): só se nomeia um plano
 * quando o plano atual é conhecido, não tem o recurso, está no catálogo
 * visível com preço, e há um plano vendável ACIMA dele com o recurso. Em
 * qualquer outro caso, nenhum plano mínimo:
 * - sem plano atual (access-state fora do ar, conta sem assinatura): quem
 *   paga o Pro veria "faça o upgrade para o Lite";
 * - plano atual que já tem o recurso: o bloqueio é por outro motivo (ex.:
 *   cancelada com o kill switch desligado), e nem o plano atual é nomeado
 *   ("Disparos não está no plano Lite" seria falso);
 * - plano atual fora do catálogo (legado) ou sem preço gravado: o preço real
 *   do legado só existe no Stripe. Pela escada só de leads o Lite R$ 399 fica
 *   "acima" do Mini legado R$ 449, e o bloqueio ofereceria um plano maior mais
 *   barato do que o cliente paga (24/09/2026). O legado vai para o comercial;
 * - preço pago não verificado pelo servidor (`currentPriceVerified` falso):
 *   price trocado à mão no Stripe faz um "Mini de R$ 180" pagar R$ 449, e a
 *   lista do plano diria que o Lite R$ 399 é upgrade.
 *
 * Mínimo (mesma escada do backend, `planLadder.ts`): o plano visível de menor
 * `leads_per_month_limit` que tem o recurso, com limite MAIOR que o do atual
 * (empate não é upgrade) e preço mensal MAIOR OU IGUAL ao atual.
 */
export function buildPlanUpgradeOffer(
  input: BuildPlanUpgradeOfferInput
): PlanUpgradeOffer {
  const { feature, plans, currentSlug, currentFeatures } = input;
  if (!currentSlug) return NO_OFFER;

  const currentPlan = plans.find((plan) => plan.slug === currentSlug);
  const ownedFeatures: SectorPlanFeatures =
    currentFeatures ?? currentPlan?.features ?? {};
  if (ownedFeatures[feature]) return NO_OFFER;

  const currentPlanName = currentPlan
    ? displayPlanName(currentPlan.name)
    : planNameFromSlug(currentSlug);
  const required = planUpgradeTarget(input);
  if (!required || !currentPlan) {
    return {
      currentPlanName,
      requiredPlanName: null,
      requiredPlanSlug: null,
      unlocks: [],
    };
  }
  const currentLeads = currentPlan.leads_per_month_limit;

  const unlocks = FEATURE_BENEFITS.filter(
    ([key]) => required.features[key] && !ownedFeatures[key]
  ).map(([, label]) => label);

  // Limite atual nulo é "sem limite": não há ganho a prometer, e a linha não
  // entra.
  for (const benefit of [
    whatsappsBenefit(
      required.whatsapp_connections_limit,
      currentPlan.whatsapp_connections_limit
    ),
    leadsBenefit(required.leads_per_month_limit, currentLeads),
  ]) {
    if (benefit) unlocks.push(benefit);
  }

  return {
    currentPlanName,
    requiredPlanName: displayPlanName(required.name),
    requiredPlanSlug: required.slug,
    unlocks,
  };
}
